/**
 * The words of "Change plan" (PlanCompare.jsx, PlanRequest.jsx's dialog) in Italian and English: Italian when the
 * browser is in Italian, English otherwise (as the emails, NotificationsService.browserEmailLocale, and the voice).
 * `planText(lang)` gives `t(key, params)`; a missing Italian key falls back to English, a missing key to the key.
 * Capabilities come from the gateway (GET /api/v1/me/plans) with an English `text`; `capabilityText` puts them in
 * the page's language by `id`, else keeps the gateway's text.
 */

export function planLanguage() {
  const language = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return language.toLowerCase().startsWith('it') ? 'it' : 'en';
}

const TABS = {
  en: { dashboard: 'Home', preview: 'Preview', code: 'Code', api: 'API', data: 'Data', traces: 'Traces', quality: 'Quality', config: 'Configuration', mail: 'External Services', mcp: 'MCP', terminal: 'Terminal', git: 'Git', history: 'History' },
  it: { dashboard: 'Home', preview: 'Anteprima', code: 'Codice', api: 'API', data: 'Dati', traces: 'Tracce', quality: 'Qualità', config: 'Configurazione', mail: 'Servizi esterni', mcp: 'MCP', terminal: 'Terminale', git: 'Git', history: 'Cronologia' },
};
const CONNECTIONS = { aiFoundry: 'AI Foundry', contextCatalog: 'Context Catalog', console: 'Console', fastData: 'Fast Data' };

const MESSAGES = {
  en: {
    changePlan: 'Change plan',
    changeRequest: 'Change my plan request',
    title: 'Change plan',
    intro: 'Compare the plans. Your sandbox admin approves a change; your files and databases are kept.',
    introOn: 'You are on {plan}.',
    estimateNote: 'Cost per hour is an estimate in credits at full reservation: what the plan reserves (CPU, memory, disk), used or not.',
    rates: '{cpu} credits per vCPU-hour · {mem} per GB-hour of memory · {disk} per GB-month of disk',
    yourPlan: 'Your plan',
    requested: 'Requested',
    loading: 'Loading the plans…',
    loadError: 'The plans could not be loaded.',
    retry: 'Try again',
    rowCost: 'Cost per hour',
    rowCostHint: 'credits, estimate',
    rowMonthly: 'Credits a month',
    rowMonthlyHint: 'included; its limits use about {n}',
    monthlyCredits: '{n} credits',
    monthlyNone: 'Not set',
    rowCpu: 'CPU',
    rowMemory: 'Memory',
    rowDisk: 'Code disk',
    rowDataDisk: 'Data disk',
    rowServices: 'Services at once',
    rowHours: 'Active hours a month',
    rowTabs: 'Tabs included',
    rowChat: 'Flow chat',
    rowTerminal: 'Terminal',
    rowDocker: 'Docker',
    rowBackups: 'Own backups',
    unlimited: 'Unlimited',
    noEstimate: 'No estimate',
    perHour: '{n} credits/h',
    vcpu: '{n} vCPU',
    hours: '{n} h',
    tabsCount: '{n} of {total}',
    backupsPerDay: '{n} a day',
    included: 'Included',
    notIncluded: 'Not included',
    whatYouGet: 'What you get',
    current: 'The plan you are on now.',
    adds: 'Adds {list}.',
    more: 'More {list}.',
    same: 'The same as your plan.',
    takesAway: 'Takes away {list}.',
    less: 'Less {list}.',
    andMore: '{list} and {n} more',
    ask: 'Ask for {plan}',
    askShort: 'Choose',
    confirmTitle: 'Ask for the {plan} plan?',
    confirmAsk: 'Your sandbox admin decides; you get the answer in Flow’s notifications. Once approved, the new limits apply the next time your sandbox wakes up.',
    confirmCost: 'Estimated cost per hour: {from} → {to} credits ({delta}).',
    confirmCostUnknown: 'Estimated cost per hour: {to}.',
    confirmGains: 'You get: {list}.',
    confirmShrinks: 'What shrinks or goes away',
    confirmShrinksNote: 'Your files and databases are kept, but you may need to free up space or stop services to fit the smaller limits.',
    confirmReplace: 'This replaces your pending request for {plan}.',
    reason: 'Why (optional)',
    reasonPlaceholder: 'e.g. I run a database and three services for a demo next week',
    send: 'Ask for {plan}',
    sending: 'Sending…',
    back: 'Back',
    cancel: 'Cancel',
    sendError: 'The request could not be sent.',
    sent: 'Asked for {plan}: your sandbox admin decides.',
    keptOverride: 'Kept for you by your sandbox admin',
    limit_cpu: 'CPU',
    limit_memoryBytes: 'memory',
    limit_diskBytes: 'code disk',
    limit_dataDiskBytes: 'data disk',
    limit_runningServices: 'services at once',
    limit_activeHoursPerMonth: 'active hours',
    'limit_backups.manualPerDay': 'backups a day',
    'limit_backups.restoresPerDay': 'restores a day',
    'limit_backups.uploadMaxMb': 'zip size to restore from',
    'limit_backups.keepLast': 'backups kept',
    'limit_backups.keepDays': 'days backups are kept',
    cap_tab: '{tab} tab',
    cap_chat: 'Flow chat with the organization’s AI models',
    cap_chatOwnKey: 'Flow chat with your own AI key',
    cap_playbooks: 'AI playbooks (agents)',
    cap_terminal: 'a terminal',
    cap_terminalSystem: 'a terminal in the system containers',
    cap_docker: 'Docker and Docker Compose',
    cap_backups: 'your own backups ({count} a day)',
    cap_restores: 'restores ({count} a day)',
    cap_zipRestore: 'restore from a zip (up to {mb} MB)',
    cap_connection: 'the {name} connection',
    creditsTitle: 'Your credits',
    creditsLeft: '{left} credits left {where}',
    creditsLeftOf: '{left} of {of} credits left {where}',
    creditsScope_cap: 'on your cap',
    creditsScope_plan: 'this month, from your plan',
    creditsScope_team: 'in your team’s budget',
    creditsScope_organization: 'in your organization’s pool',
    creditsSpent: '{n} spent this month',
    creditsBurn: '{n} a day',
    creditsDaysLeft: 'about {n} days left at this rate (an estimate)',
    rowLasts: 'Your credits last',
    rowLastsHint: 'if it ran all day, estimate',
    lastsNone: 'Used up',
    lastsOverYear: 'over a year',
    lastsHours: 'about {n} h',
    lastsDays: 'about {n} days',
    creditsNoteChange: 'Credits are bought by your organization’s billing admin; changing plan changes what a sandbox hour costs.',
    creditsLowTitle: 'Your credits are running low',
    creditsLowText: '{line}. Nothing stops yet: ask your billing admin to add credits.',
    creditsHoldTitle: 'Your credits are used up',
    creditsHoldText: 'New work, like starting your sandbox, and Chat wait until credits are added. Ask your billing admin. Your files and databases are kept.',
    creditsBlockedText: 'Your credits are used up. Ask your billing admin to add credits.',
    creditsRecheck: 'Check again',
  },
  it: {
    changePlan: 'Cambia piano',
    changeRequest: 'Modifica la richiesta di piano',
    title: 'Cambia piano',
    intro: 'Confronta i piani. Il cambio lo approva l’amministratore della sandbox; file e database restano.',
    introOn: 'Il tuo piano è {plan}.',
    estimateNote: 'Il costo orario è una stima in crediti a piena prenotazione: ciò che il piano riserva (CPU, memoria, disco), usato o no.',
    rates: '{cpu} crediti per vCPU-ora · {mem} per GB-ora di memoria · {disk} per GB-mese di disco',
    yourPlan: 'Il tuo piano',
    requested: 'Richiesto',
    loading: 'Caricamento dei piani…',
    loadError: 'Non è stato possibile caricare i piani.',
    retry: 'Riprova',
    rowCost: 'Costo orario',
    rowCostHint: 'crediti, stima',
    rowMonthly: 'Crediti al mese',
    rowMonthlyHint: 'inclusi; i suoi limiti ne usano circa {n}',
    monthlyCredits: '{n} crediti',
    monthlyNone: 'Non impostati',
    rowCpu: 'CPU',
    rowMemory: 'Memoria',
    rowDisk: 'Disco del codice',
    rowDataDisk: 'Disco dei dati',
    rowServices: 'Servizi in contemporanea',
    rowHours: 'Ore attive al mese',
    rowTabs: 'Schede incluse',
    rowChat: 'Chat di Flow',
    rowTerminal: 'Terminale',
    rowDocker: 'Docker',
    rowBackups: 'Backup personali',
    unlimited: 'Illimitato',
    noEstimate: 'Nessuna stima',
    perHour: '{n} crediti/h',
    vcpu: '{n} vCPU',
    hours: '{n} h',
    tabsCount: '{n} su {total}',
    backupsPerDay: '{n} al giorno',
    included: 'Incluso',
    notIncluded: 'Non incluso',
    whatYouGet: 'Cosa ottieni',
    current: 'Il piano che hai ora.',
    adds: 'Aggiunge {list}.',
    more: 'Più {list}.',
    same: 'Uguale al tuo piano.',
    takesAway: 'Toglie {list}.',
    less: 'Meno {list}.',
    andMore: '{list} e altri {n}',
    ask: 'Richiedi {plan}',
    askShort: 'Scegli',
    confirmTitle: 'Richiedere il piano {plan}?',
    confirmAsk: 'Decide l’amministratore della sandbox; la risposta arriva nelle notifiche di Flow. Una volta approvato, i nuovi limiti valgono dal prossimo risveglio della sandbox.',
    confirmCost: 'Costo orario stimato: {from} → {to} crediti ({delta}).',
    confirmCostUnknown: 'Costo orario stimato: {to}.',
    confirmGains: 'Ottieni: {list}.',
    confirmShrinks: 'Cosa si riduce o sparisce',
    confirmShrinksNote: 'File e database restano, ma potresti dover liberare spazio o fermare dei servizi per stare nei limiti più piccoli.',
    confirmReplace: 'Sostituisce la tua richiesta in attesa per {plan}.',
    reason: 'Perché (facoltativo)',
    reasonPlaceholder: 'es. mi servono un database e tre servizi per una demo la prossima settimana',
    send: 'Richiedi {plan}',
    sending: 'Invio…',
    back: 'Indietro',
    cancel: 'Annulla',
    sendError: 'Non è stato possibile inviare la richiesta.',
    sent: 'Hai richiesto {plan}: decide l’amministratore della sandbox.',
    keptOverride: 'Mantenuto per te dall’amministratore della sandbox',
    limit_cpu: 'CPU',
    limit_memoryBytes: 'memoria',
    limit_diskBytes: 'disco del codice',
    limit_dataDiskBytes: 'disco dei dati',
    limit_runningServices: 'servizi in contemporanea',
    limit_activeHoursPerMonth: 'ore attive',
    'limit_backups.manualPerDay': 'backup al giorno',
    'limit_backups.restoresPerDay': 'ripristini al giorno',
    'limit_backups.uploadMaxMb': 'dimensione dello zip da ripristinare',
    'limit_backups.keepLast': 'backup conservati',
    'limit_backups.keepDays': 'giorni di conservazione dei backup',
    cap_tab: 'la scheda {tab}',
    cap_chat: 'la chat di Flow con i modelli AI dell’organizzazione',
    cap_chatOwnKey: 'la chat di Flow con la tua chiave AI',
    cap_playbooks: 'i playbook AI (agenti)',
    cap_terminal: 'un terminale',
    cap_terminalSystem: 'un terminale nei container di sistema',
    cap_docker: 'Docker e Docker Compose',
    cap_backups: 'i tuoi backup ({count} al giorno)',
    cap_restores: 'i ripristini ({count} al giorno)',
    cap_zipRestore: 'il ripristino da zip (fino a {mb} MB)',
    cap_connection: 'il collegamento a {name}',
    creditsTitle: 'I tuoi crediti',
    creditsLeft: '{left} crediti rimasti {where}',
    creditsLeftOf: '{left} di {of} crediti rimasti {where}',
    creditsScope_cap: 'nel tuo tetto',
    creditsScope_plan: 'questo mese, dal tuo piano',
    creditsScope_team: 'nel budget del tuo team',
    creditsScope_organization: 'nel fondo della tua organizzazione',
    creditsSpent: '{n} spesi questo mese',
    creditsBurn: '{n} al giorno',
    creditsDaysLeft: 'circa {n} giorni a questo ritmo (una stima)',
    rowLasts: 'I tuoi crediti durano',
    rowLastsHint: 'se andasse tutto il giorno, stima',
    lastsNone: 'Esauriti',
    lastsOverYear: 'più di un anno',
    lastsHours: 'circa {n} h',
    lastsDays: 'circa {n} giorni',
    creditsNoteChange: 'I crediti li compra l’amministratore della fatturazione della tua organizzazione; cambiare piano cambia quanto costa un’ora di sandbox.',
    creditsLowTitle: 'I tuoi crediti stanno finendo',
    creditsLowText: '{line}. Per ora nulla si ferma: chiedi all’amministratore della fatturazione di aggiungere crediti.',
    creditsHoldTitle: 'I tuoi crediti sono finiti',
    creditsHoldText: 'Il lavoro nuovo, come avviare la sandbox, e la chat aspettano che vengano aggiunti crediti. Chiedi all’amministratore della fatturazione. File e database restano.',
    creditsBlockedText: 'I tuoi crediti sono finiti. Chiedi all’amministratore della fatturazione di aggiungere crediti.',
    creditsRecheck: 'Controlla di nuovo',
  },
};

const fill = (text, params = {}) => String(text).replace(/\{(\w+)\}/g, (all, name) => (params[name] === undefined ? all : String(params[name])));

/** `t(key, params)` in `lang` (default: the browser's). */
export function planText(lang = planLanguage()) {
  const words = MESSAGES[lang] || MESSAGES.en;
  return (key, params) => fill(words[key] ?? MESSAGES.en[key] ?? key, params);
}

export const tabName = (id, lang = planLanguage()) => (TABS[lang] || TABS.en)[id] || id;

/** A capability `{ id, text, params }` from the gateway in the page's language. */
export function capabilityText(cap, lang = planLanguage()) {
  const t = planText(lang);
  const id = String(cap?.id || '');
  if (id.startsWith('tab:')) { return t('cap_tab', { tab: tabName(id.slice(4), lang) }); }
  if (id.startsWith('connection:')) { return t('cap_connection', { name: CONNECTIONS[id.slice(11)] || id.slice(11) }); }
  const key = `cap_${id}`;
  return MESSAGES.en[key] ? t(key, cap.params || {}) : cap?.text || id;
}

export const TAB_ORDER = ['dashboard', 'preview', 'code', 'api', 'data', 'traces', 'quality', 'config', 'mail', 'mcp', 'terminal', 'git', 'history'];

export { MESSAGES };
